import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { MaskLines, Eyebrow } from "@/components/Motion";

export const Hero = () => {
  const { t, lp } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.2]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const h = t.home;

  return (
    <section ref={ref} data-testid="home-hero" className="relative flex min-h-[100svh] items-end overflow-hidden bg-[#0F2138]">
      <motion.div style={{ y, scale }} className="absolute inset-0 origin-top">
        <motion.img src="/img/hero.jpg" alt="Port cranes over a container terminal at dusk" fetchpriority="high"
          initial={{ clipPath: "inset(12% 8% 12% 8%)" }} animate={{ clipPath: "inset(0% 0% 0% 0%)" }} transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          className="h-full w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-[#0F2138]/60" aria-hidden="true" />
      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-[#0F2138]/50 [mask-image:linear-gradient(to_top,black,transparent)]" aria-hidden="true" />
      <div className="grain" aria-hidden="true" />

      <motion.div style={{ y: textY, opacity: fade }} className="relative mx-auto w-full max-w-7xl px-5 pb-16 pt-36 sm:px-8 sm:pb-24 lg:px-12">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }}>
          <Eyebrow light>{h.eyebrow}</Eyebrow>
        </motion.div>
        <h1 className="mt-7 max-w-5xl font-serif text-[2.6rem] font-medium leading-[1.04] tracking-tight text-white sm:text-6xl lg:text-7xl xl:text-[5.4rem]" data-testid="hero-headline">
          <MaskLines lines={h.heroLines} delay={0.35} lineClass="[&:nth-child(1)]:text-white" />
        </h1>
        <div className="mt-10 grid gap-10 border-t border-white/20 pt-8 lg:grid-cols-12">
          <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0, duration: 0.9 }}
            className="max-w-xl text-base leading-relaxed text-[#D6DEE8] md:text-lg lg:col-span-6" data-testid="hero-subheadline">{h.heroSub}</motion.p>
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.9 }}
            className="flex flex-col gap-3 sm:flex-row lg:col-span-6 lg:justify-end lg:self-start">
            <Link to={lp("/services")} className="btn-ghost-light justify-center" data-testid="hero-services-button">{t.common.ourServices}</Link>
            <Link to={lp("/quote")} className="btn-brass justify-center" data-testid="hero-quote-button">{t.common.requestQuote}<ArrowRight className="h-4 w-4 rtl:-scale-x-100" /></Link>
          </motion.div>
        </div>
      </motion.div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8 }} className="absolute bottom-6 end-6 hidden items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#C9D4E1] lg:flex" aria-hidden="true">
        {h.scroll}<ArrowDown className="h-4 w-4 animate-bounce" />
      </motion.div>
    </section>
  );
};
